/**
 * Customer-facing order hooks — single order detail + paginated history.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { pickI18nBlob } from "@/components/admin/I18nField";
import { apiFetch } from "@/lib/api";
import type {
  CustomerDispatch,
  OrderCancelReason,
  OrderDetail,
  OrderMaterialCertificate,
  OrderPaymentInstructions,
} from "@/api/types";

import { queryKeys } from "./keys";

const API = "/api/v1";

export function useOrder(
  orderNumber: string | null,
  options?: Omit<
    UseQueryOptions<OrderDetail>,
    "queryKey" | "queryFn" | "enabled"
  > & {
    /** UI language — the BE labels ``dimension_labels`` in it (KAN-926).
     *  Part of the query key so switching language refetches. */
    lang?: string;
  },
) {
  const { lang, ...rest } = options ?? {};
  return useQuery<OrderDetail>({
    queryKey: lang
      ? [...queryKeys.order(orderNumber ?? ""), lang]
      : queryKeys.order(orderNumber ?? ""),
    queryFn: () =>
      apiFetch<OrderDetail>(`${API}/orders/${orderNumber}`, { lang }),
    enabled: Boolean(orderNumber),
    staleTime: 60 * 1000,
    ...rest,
  });
}

/**
 * How to pay an unpaid order by transfer (KAN-764).
 *
 * Only called for orders the shop cannot charge a card for — the
 * endpoint ISSUES the proforma when it does not exist yet, so asking
 * for it is not free of consequence and must not happen on every
 * thanks-page render. ``enabled`` is the whole gate; pass it false
 * whenever the card path is live or the order is already paid.
 *
 * 409 ``order_not_awaiting_payment`` is a legitimate answer (the order
 * got paid between render and fetch), not a failure to retry.
 */
export function useOrderPaymentInstructions(
  orderNumber: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery<OrderPaymentInstructions>({
    queryKey: queryKeys.orderPaymentInstructions(orderNumber ?? ""),
    queryFn: () =>
      apiFetch<OrderPaymentInstructions>(
        `${API}/orders/${orderNumber}/payment-instructions`,
      ),
    enabled: Boolean(orderNumber) && (options?.enabled ?? true),
    retry: false,
    staleTime: 60 * 1000,
  });
}

/** Owner-visible per-shipment split/merge view (mirrors the /track list). */
export interface CustomerShipment {
  note_number: string | null;
  status: string;
  pieces: number;
  tracking_code: string | null;
  tracking_url: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
}

export interface MyOrderShipments {
  fulfillment: "unshipped" | "partial" | "fulfilled";
  shipments: CustomerShipment[];
  /** KAN-1882 — kolik kusů odešlo z kolika a co se dovyrábí. */
  dispatch?: CustomerDispatch | null;
  /** KAN-2015 — osobní odběr: stavy čte slovník odběru (``lib/pickupVocabulary``). */
  personal_pickup?: boolean;
}

export function useMyOrderShipments(
  orderNumber: string | null,
  options?: Omit<
    UseQueryOptions<MyOrderShipments>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery<MyOrderShipments>({
    queryKey: queryKeys.orderShipments(orderNumber ?? ""),
    queryFn: () =>
      apiFetch<MyOrderShipments>(`${API}/orders/${orderNumber}/shipments`),
    enabled: Boolean(orderNumber),
    staleTime: 60 * 1000,
    ...options,
  });
}

interface OrderMaterialCertificatesResponse {
  items: OrderMaterialCertificate[];
}

export function useOrderMaterialCertificates(
  orderNumber: string | null,
  options?: Omit<
    UseQueryOptions<OrderMaterialCertificatesResponse>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery<OrderMaterialCertificatesResponse>({
    queryKey: ["orders", orderNumber, "material-certificates"],
    queryFn: () =>
      apiFetch<OrderMaterialCertificatesResponse>(
        `${API}/orders/${orderNumber}/material-certificates`,
      ),
    enabled: Boolean(orderNumber),
    staleTime: 60 * 1000,
    ...options,
  });
}

// --- Customer order history: GET /orders -----------------------------
//
// Scoped server-side to ``user_id = current_user.id`` — see the
// endpoint docstring. Anonymous (guest) checkouts don't surface here;
// they're only reachable via the per-order URL in the confirmation
// e-mail. The BE returns a flat ``MyOrderRow`` (no embedded items);
// the detail page (``useOrder``) stays the single source for full
// order trees.

interface MyOrderRow {
  order_number: string;
  uid: string | null;
  /** KAN-1430 — veřejné číslo objednávky ``xxx-xxx-xxx``; zobrazuje ho ``displayId``. */
  public_number: string | null;
  status: string;
  total_czk: number;
  currency: string;
  fx_rate_to_czk: number;
  created_at: string;
  paid_at: string | null;
}

interface MyOrdersListResponse {
  total: number;
  page: number;
  page_size: number;
  items: MyOrderRow[];
}

interface UseMyOrdersParams {
  page?: number;
  pageSize?: number;
}

export function useMyOrders(
  params: UseMyOrdersParams = {},
  options?: Omit<UseQueryOptions<MyOrdersListResponse>, "queryKey" | "queryFn">,
) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  return useQuery<MyOrdersListResponse>({
    queryKey: ["auth", "my-orders", page, pageSize],
    queryFn: () =>
      apiFetch<MyOrdersListResponse>(
        `${API}/orders?page=${page}&page_size=${pageSize}`,
      ),
    // A newly-placed order shouldn't take 60 seconds to surface on
    // the history page, but we also don't need second-resolution
    // freshness here — the thank-you page is the live view, this is
    // a receipt browser.
    staleTime: 10 * 1000,
    retry: false,
    ...options,
  });
}

// --- Quality reporting: defects + final grade (PR-63) ----------------
//
// Defects are claimed on the order page (KAN-1672, ``/orders/{number}/claims``
// — see ``useCreateOrderClaim``). The final QC grade is a separate
// one-shot endpoint: ``POST /orders/{number}/quality-report`` (BE
// returns 409 on the second call so the FE doesn't need to model
// "edit existing"). Both routes share the same auth gate as
// ``GET /orders/{number}``: admin / signed-in owner / guest cookie.

interface QualityReportPayload {
  order_number: string;
  grade: string;
  notes?: string | null;
}

interface QualityReportOut {
  id: number;
  order_id: number;
  grade: string;
}

export function useSubmitQualityReport(
  options?: Omit<
    UseMutationOptions<QualityReportOut, Error, QualityReportPayload>,
    "mutationFn"
  >,
) {
  return useMutation<QualityReportOut, Error, QualityReportPayload>({
    mutationFn: ({ order_number, ...body }) =>
      apiFetch<QualityReportOut>(
        `${API}/orders/${order_number}/quality-report`,
        { method: "POST", body },
      ),
    ...options,
  });
}

// --- Reorder: POST /orders/{n}/reorder -------------------------------
//
// Team-aware reorder. Clones the source order
// into the caller's draft cart — accepts both self-orders and any
// CustomerOrg-team-mate's order. The response carries per-line
// outcome so the FE can summarise "X added, Y skipped (material no
// longer available)" without a second round-trip.

export interface ReorderSkipped {
  file_name: string;
  reason: string;
  /** Stable code for i18n mapping: ``material_inactive`` /
   * ``variant_inactive`` / ``finish_inactive`` / ``unavailable``. */
  code: string;
}

export interface ReorderResponse {
  order_id: number;
  order_number: string;
  added_lines: number;
  skipped_lines: ReorderSkipped[];
}

export function useReorder(
  options?: Omit<
    UseMutationOptions<ReorderResponse, Error, string>,
    "mutationFn"
  >,
) {
  return useMutation<ReorderResponse, Error, string>({
    mutationFn: (orderNumber) =>
      apiFetch<ReorderResponse>(`${API}/orders/${orderNumber}/reorder`, {
        method: "POST",
      }),
    ...options,
  });
}

// --- KAN-1641 — zrušení objednávky zákazníkem ---------------------------

/** Důvody, které smí zákazník uvést (číselník na serveru, popisky v jazyce). */
export function useCustomerCancelReasons(lang: string, options?: { enabled?: boolean }) {
  return useQuery<OrderCancelReason[]>({
    queryKey: ["orders", "cancel-reasons", lang],
    queryFn: () => apiFetch<OrderCancelReason[]>(`${API}/orders/cancel-reasons`, { lang }),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 60 * 1000,
  });
}

export interface CustomerCancelVars {
  orderNumber: string;
  reason: string;
  note?: string;
  /** ``cancel`` = zrušit nezaplacenou, ``request`` = požádat obchod. */
  kind: "cancel" | "request";
}

export function useCustomerCancelOrder(
  options?: Omit<
    UseMutationOptions<{ order_number: string; status: string }, Error, CustomerCancelVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<{ order_number: string; status: string }, Error, CustomerCancelVars>({
    mutationFn: ({ orderNumber, reason, note, kind }) =>
      apiFetch(
        `${API}/orders/${encodeURIComponent(orderNumber)}/${kind === "cancel" ? "cancel" : "cancel-request"}`,
        { method: "POST", body: { reason, note: note || null } },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // KAN-1784 — stránka může být otevřená pod veřejným číslem, mutace
      // jde pod technickým; klíče se nerovnají, proto celý prefix „order".
      await qc.invalidateQueries({ queryKey: queryKeys.order(variables.orderNumber) });
      await qc.invalidateQueries({ queryKey: ["order"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- Reklamace (KAN-1672) ---------------------------------------------

/** Stav reklamace — ``GET /orders/{n}/claims`` (KAN-1672). */
export type OrderClaimStatus = "new" | "in_progress" | "resolved" | "rejected";

export interface OrderClaim {
  id: number;
  status: OrderClaimStatus;
  created_at: string;
  order_number: string | null;
  description: string;
  /** ``quantity`` = reklamované (vadné) kusy z ``ordered_quantity`` objednaných (KAN-1866). */
  items: {
    id: number;
    slug: string;
    file_name: string | null;
    quantity: number;
    ordered_quantity: number;
  }[];
  photos: { file_name: string; url: string }[];
  /** Typ vady z platformního katalogu (KAN-1736); ``null`` = bez typu. */
  defect_type: ClaimDefectType | null;
  /** KAN-1880 — jak obchod reklamaci vyřídil; ``null`` = nerozhodnuto. */
  resolution?: "remake" | "refund" | "reject" | null;
  /** Šarže náhradní výroby; ``done`` = kusy jdou poslat jako náhrada. */
  lots?: { item_id: number; lot_key: string; quantity: number; done: boolean }[];
  /** Kolik kusů jde teď poslat jako náhradu (hotové − odeslané). */
  replacement_capacity?: { order_line_id: number; quantity: number }[];
  /** Návrh vratky za reklamované kusy, MINOR jednotky ``currency_code``. */
  refund_proposal_minor?: number;
  currency_code?: string | null;
}

/**
 * Typ vady — ``GET /defect-types`` (jediný platformní katalog, KAN-1736;
 * z téhož čte i aplikace Kvalita). U reklamace popisky ``null`` = typ
 * mezitím z katalogu zmizel.
 */
export interface ClaimDefectType {
  code: string;
  label_cs: string | null;
  label_en: string | null;
  /** Název per jazyk platformy (KAN-1804); starší odpověď ho nemá. */
  label_i18n?: Record<string, string>;
}

/** Popisek typu vady v jazyce UI; chybí-li překlad, výchozí jazyk, pak cokoli, pak kód. */
export function defectTypeLabel(dt: ClaimDefectType, lang: string): string {
  return pickI18nBlob(dt.label_i18n, dt.label_cs, dt.label_en, lang) || dt.code;
}

export function useDefectTypes(options?: { enabled?: boolean }) {
  return useQuery<ClaimDefectType[]>({
    queryKey: ["defect-types"],
    queryFn: () => apiFetch<ClaimDefectType[]>(`${API}/defect-types`),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

/** Co z reklamace vidí zákazník — ``PublicClaimOut`` (docs/API.md → Public responses). */
export interface CustomerOrderClaim {
  id: number;
  status: OrderClaimStatus;
  items: { slug: string; file_name: string | null }[];
}

export function useOrderClaims(orderNumber: string, options?: { enabled?: boolean }) {
  return useQuery<CustomerOrderClaim[]>({
    queryKey: ["orders", "claims", orderNumber],
    queryFn: () => apiFetch<CustomerOrderClaim[]>(`${API}/orders/${encodeURIComponent(orderNumber)}/claims`),
    enabled: options?.enabled ?? true,
    staleTime: 30 * 1000,
  });
}

export interface CreateOrderClaimVars {
  orderNumber: string;
  /** Veřejné klíče položek (``OrderItem.slug``, KAN-1957). */
  itemSlugs: string[];
  /** Počet vadných kusů, index po indexu s ``itemSlugs`` (KAN-1866); bez něj = celé množství. */
  quantities?: number[];
  /** Kód z ``useDefectTypes`` (KAN-1736). */
  defectType: string;
  description: string;
  photos: File[];
}

export function useCreateOrderClaim(
  options?: Omit<UseMutationOptions<CustomerOrderClaim, Error, CreateOrderClaimVars>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<CustomerOrderClaim, Error, CreateOrderClaimVars>({
    mutationFn: ({ orderNumber, itemSlugs, quantities, defectType, description, photos }) => {
      const body = new FormData();
      body.append("description", description);
      if (defectType) body.append("defect_type", defectType);
      for (const slug of itemSlugs) body.append("item_slugs", slug);
      for (const qty of quantities ?? []) body.append("item_quantities", String(qty));
      for (const photo of photos) body.append("photos", photo);
      return apiFetch<CustomerOrderClaim>(`${API}/orders/${encodeURIComponent(orderNumber)}/claims`, {
        method: "POST",
        body,
      });
    },
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      await qc.invalidateQueries({ queryKey: ["orders", "claims", variables.orderNumber] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}
